import { useState, useEffect } from "react";

/**
 * Custom hook for handling a countdown timer with focus visibility management
 * @param {number} initialTime - The initial time for the countdown in seconds.
 * @param {boolean} isLoading - A flag indicating whether the timer should be paused due to loading.
 * @param {function} onTimerEnd - Callback function to call when the timer reaches zero.
 * @returns {number} timeLeft - The remaining time in seconds.
 */
function useTimer(initialTime=0, isLoading, onTimerEnd = () => {}) {
  const [timeLeft, setTimeLeft] = useState(initialTime);
  let intervalId;
  let backgroundStartTime;
  
  useEffect(() => {
    if (isLoading || !initialTime || initialTime <= 0) return;

    const updateCounter = () => {
        setTimeLeft((prevTime) => {
        const newTime = prevTime - 1;
        if (newTime <= 0) {
          onTimerEnd(); // Call the callback when the timer ends
          clearInterval(intervalId); // Clear the interval when timer ends
          document.removeEventListener("visibilitychange", handleVisibilityChange); // Cleanup event listener
          return 0;
        }
        return newTime;
      });
    };
    clearInterval(intervalId); // Clear any existing interval
    setTimeLeft(initialTime)
    intervalId = setInterval(updateCounter, 1000);
    const handleVisibilityChange = () => {
      if (document.hidden) {
        backgroundStartTime = performance.now(); // Track time when backgrounded
        clearInterval(intervalId); // Pause counter when in background
      } else {
        const backgroundElapsedTime = Math.ceil(
          (performance.now() - backgroundStartTime) / 1000
        ); // Calculate background time in seconds
        setTimeLeft((prevTime) => prevTime - backgroundElapsedTime); // Adjust timeLeft
        intervalId = setInterval(updateCounter, 1000); // Resume counter
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    // Cleanup on component unmount
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      clearInterval(intervalId); // Cleanup interval on unmount
    };
  }, [isLoading, initialTime]);

  return timeLeft;
}

export default useTimer;
